import styled from 'styled-components';
import { allProps, Box, Colors, CustomTheme, Sizes } from '@altermaind/web/ui';
import { convertColorToRgba } from '@/_shared/helpers/convertColorToRgba';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';

export const AreasBar = styled(Box)(({ theme }) => {
  const gap = (theme.space as Sizes).xs;
  const { isMobile } = useBreakingPoints();
  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexDirection: isMobile ? 'column' : 'row',
    gap,
  };
}, allProps);

export const MacroAreaDot = styled(Box)<{
  highlight: boolean;
  active: boolean;
  highlightColor?: keyof Colors;
}>(({ theme, active, highlight, highlightColor = 'primary' }) => {
  const bgHighlight = convertColorToRgba(
    (theme.colors as keyof CustomTheme['colors'])[highlightColor],
    1,
  );
  const bgActive = convertColorToRgba((theme.colors as Colors)['primary/text'], 1);
  const bgNotActive = convertColorToRgba((theme.colors as Colors)['grey/light/background'], 1);
  const bgNotHighlight = active ? bgActive : bgNotActive;

  const backgroundColor = highlight ? bgHighlight : bgNotHighlight;
  return `
      border-radius: 50%;
      margin: auto;
      background: ${backgroundColor};
      transition: background 0.3s;
    `;
}, allProps);

MacroAreaDot.defaultProps = {
  width: 'xxs',
  height: 'xxs',
  mb: 'xxs',
};

export const AreaProgressWrapper = styled(Box)<{ steps: number }>(({ theme, steps }) => {
  const { isMobile } = useBreakingPoints();

  const microStepSize = 90; // size of each step "inside" the progress bar
  const spacing = convertSizeToNumber(theme?.space.xs) || 0; // spacing between steps
  const width = steps * microStepSize + spacing;
  return `
    width: ${isMobile ? '100%' : `${width}px`};
    margin: 0 auto;
  `;
}, allProps);

export const AreaProgressBarBg = styled(Box)<{ active?: boolean }>(({ theme, active }) => {
  const bgActive = convertColorToRgba((theme.colors as Colors)['primary/text'], 1);
  const bgNotActive = convertColorToRgba((theme.colors as Colors)['grey/light/background'], 1);
  return `
    position: relative;
    width: 100%;
    background: ${active ? bgActive : bgNotActive};
  `;
}, allProps);

AreaProgressBarBg.defaultProps = {
  borderRadius: 'xs',
  marginBottom: 'xs',
  height: 'xxs',
};

export const AreaProgressBarFg = styled(Box)<{ percent: number }>(({ percent }) => {
  return `
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: ${percent}%;
    transition: width 0.4s cubic-bezier(0.68, -0.55, 0.27, 1.55);
    height: 100%;
  `;
}, allProps);

AreaProgressBarFg.defaultProps = {
  bg: 'primary',
  borderRadius: 'xs',
};
